import { postLegacy, LEGACY_BASE, V2_BASE } from './client';
const MAILER_ENDPOINT = '/contact_mailer.php';
const MAILER_URL = `${LEGACY_BASE}${MAILER_ENDPOINT}`;

function appError(message, options = {}) {
  const err = new Error(message);
  err.endpoint = options.endpoint || '';
  err.isValidation = Boolean(options.isValidation);
  err.isTerminal = Boolean(options.isTerminal);
  return err;
}

function normalizeApiError(error, fallbackMessage) {
  const response = error?.response;
  const payload = response?.data;
  const endpoint = error?.endpoint || `${error?.config?.baseURL || V2_BASE}${error?.config?.url || ''}`;

  if (payload && typeof payload === 'object') {
    const details = Array.isArray(payload.errors) ? payload.errors.filter(Boolean).join('; ') : '';
    const message = payload.message || payload.error || fallbackMessage;
    return `${message}${details ? ` (${details})` : ''}`;
  }

  if (typeof payload === 'string' && payload.trim()) {
    return `${fallbackMessage}: ${payload.slice(0, 180)} [endpoint: ${endpoint}]`;
  }

  if (response?.status) {
    return `${fallbackMessage}: HTTP ${response.status} [endpoint: ${endpoint}]`;
  }

  return `${error?.message || fallbackMessage} [endpoint: ${endpoint}]`;
}

export async function submitContactForm(data) {
  let mailerError = '';
  console.log('[contact] submit start', {
    endpoint: MAILER_URL,
    payload: {
      name: data?.name || '',
      email: data?.email || '',
      subject: data?.subject || '',
      hasMessage: Boolean((data?.message || '').trim()),
    },
  });

  try {
    const res = await postLegacy(MAILER_ENDPOINT, {
      action: 'submit',
      data,
    });

    if (typeof res === 'string') {
      throw appError(`Unexpected non-JSON response: ${res.slice(0, 220)} [endpoint: ${MAILER_URL}]`, {
        endpoint: MAILER_URL,
      });
    }

    if (!res || typeof res !== 'object') {
      throw appError(`Unexpected response shape from contact API [endpoint: ${MAILER_URL}]`, {
        endpoint: MAILER_URL,
      });
    }

    if (res?.status === 'success') {
      console.log('[contact] submit success (mailer)', res);
      return res;
    }

    const details = Array.isArray(res?.errors) ? ` (${res.errors.join('; ')})` : '';
    const message = (res?.message || 'Unable to submit') + details;
    throw appError(message, {
      endpoint: MAILER_URL,
      isValidation: String(res?.message || '').toLowerCase().includes('validation'),
      isTerminal: true,
    });
  } catch (error) {
    if (error?.response?.data && typeof error.response.data === 'object') {
      const terminalMessage = normalizeApiError(error, 'Unable to submit (mailer)');
      throw appError(terminalMessage, {
        endpoint: MAILER_URL,
        isTerminal: true,
      });
    }

    if (error?.isValidation) {
      throw error;
    }

    if (error?.isTerminal) {
      throw error;
    }

    mailerError = normalizeApiError(error, 'Unable to submit (mailer)');
    console.error('[contact] submit error (mailer)', mailerError);
  }

  try {
    const fallbackData = {
      ...data,
      subject: String(data?.subject || '').trim() || 'Website Inquiry',
      phone: String(data?.phone || '').trim() || 'Not provided',
    };
    const legacy = await postLegacy('/contact_crud.php', {
      status: 'submit',
      data: fallbackData,
    });

    const parsed = typeof legacy === 'string' ? parseLegacyStringPayload(legacy) : legacy;
    if (parsed?.success && !parsed?.error) {
      console.log('[contact] submit success (legacy)', parsed);
      return {
        status: 'success',
        message: parsed.success,
        data: null,
      };
    }

    const msg = parsed?.error || parsed?.message || 'Unable to submit (legacy)';
    throw new Error(`${msg} [endpoint: ${LEGACY_BASE}/contact_crud.php]`);
  } catch (fallbackError) {
    const fallbackMsg = normalizeApiError(fallbackError, 'Unable to submit (legacy)');
    console.error('[contact] submit error (legacy)', fallbackMsg, '| trace:', mailerError);
    throw new Error(`${fallbackMsg} | trace: ${mailerError}`);
  }
}

export async function submitNewsletter(email) {
  let mailerError = '';

  try {
    const res = await postLegacy(MAILER_ENDPOINT, {
      action: 'subscribe',
      data: { email },
    });

    if (typeof res === 'string') {
      throw appError(`Unexpected non-JSON response: ${res.slice(0, 220)} [endpoint: ${MAILER_URL}]`, {
        endpoint: MAILER_URL,
      });
    }

    if (!res || typeof res !== 'object') {
      throw appError(`Unexpected response shape from newsletter API [endpoint: ${MAILER_URL}]`, {
        endpoint: MAILER_URL,
      });
    }

    if (res?.status === 'success') {
      return res;
    }

    const details = Array.isArray(res?.errors) ? ` (${res.errors.join('; ')})` : '';
    const message = (res?.message || 'Unable to subscribe') + details;
    throw appError(message, {
      endpoint: MAILER_URL,
      isValidation: String(res?.message || '').toLowerCase().includes('validation'),
      isTerminal: true,
    });
  } catch (error) {
    if (error?.response?.data && typeof error.response.data === 'object') {
      const terminalMessage = normalizeApiError(error, 'Unable to subscribe (mailer)');
      throw appError(terminalMessage, {
        endpoint: MAILER_URL,
        isTerminal: true,
      });
    }

    if (error?.isValidation) {
      throw error;
    }

    if (error?.isTerminal) {
      throw error;
    }

    mailerError = normalizeApiError(error, 'Unable to subscribe (mailer)');
  }

  try {
    const legacy = await postLegacy('/contact_crud.php', {
      status: 'subscribe',
      data: { email },
    });

    const parsed = typeof legacy === 'string' ? parseLegacyStringPayload(legacy) : legacy;
    if (parsed?.success && !parsed?.error) {
      return {
        status: 'success',
        message: parsed.success,
        data: null,
      };
    }

    const msg = parsed?.error || parsed?.message || 'Unable to subscribe (legacy)';
    throw new Error(`${msg} [endpoint: ${LEGACY_BASE}/contact_crud.php]`);
  } catch (fallbackError) {
    const fallbackMsg = normalizeApiError(fallbackError, 'Unable to subscribe (legacy)');
    throw new Error(`${fallbackMsg} | trace: ${mailerError}`);
  }
}

function parseLegacyStringPayload(value) {
  if (typeof value !== 'string') {
    return value;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  try {
    return JSON.parse(trimmed);
  } catch (error) {
    return { error: `Unexpected legacy response: ${trimmed.slice(0, 200)}` };
  }
}
